import { THREE } from 'uweb/three'
import { log, KeyValue } from 'utils/web'
import { Coordinate, UTM } from 'uweb/utils'
import { CSV_GeoJson_Parser, expandBoundingBox } from '../hooks/helper'

const createTextLabel = (text: string, color = '#444', size = 1) => {
    const canvas = document.createElement('canvas');
    const context: any = canvas.getContext('2d');

    // 1. Canvas-ийн нягтаршлыг өндөр хэвээр (high-res) үлдээх
    canvas.width = 600;  // Өргөн
    canvas.height = 200; // Өндөр

    // Тунгалаг дэвсгэр
    context.clearRect(0, 0, canvas.width, canvas.height);

    // 2. Том фонтоор тод зурах (Чанарыг хадгалахын тулд)
    context.fillStyle = color;
    context.font = 'Bold 40px Arial'; // 10px биш, том зураад дараа нь масштабаар багасгана
    context.textAlign = 'center';
    context.textBaseline = 'middle';
    context.fillText(text, canvas.width / 2, canvas.height / 2);

    const texture = new THREE.CanvasTexture(canvas);

    // Анизотропи нэмэх (текстийг илүү тод харагдуулна)
    texture.anisotropy = 16;

    const spriteMaterial = new THREE.SpriteMaterial({
        map: texture,
        transparent: true
    });

    const sprite = new THREE.Sprite(spriteMaterial);

    // 3. Sprite-ийн хэмжээг энд л жижиг болгоно
    // 0.1 эсвэл 0.05 гэх мэт утгаар тоглож үзээрэй
    sprite.scale.set(0.18 * size, 0.06 * size, 1);

    return sprite;
}

const LAYER_PRESETS: any = {
    'target': '#333',
    'actual': 'red',
    'Хоосон чулуулаг': '#d48806',
    'Элсэн чулуу': '#a89030',
    'Шавар': '#8c6a3f',
    'Нүүрс': '#262626',
    'Завсар үе': '#389e0d',
    'Шавран чулуу': '#cf1322',
    'Хатуу чулуулаг': '#434343',
    'Ус': '#0284c7',
}

export const pointMaterial = (from: string, to: string, size: number = 4) => {

    const canvas = document.createElement('canvas')
    const texSize = 16
    canvas.width = texSize
    canvas.height = texSize

    const ctx: any = canvas.getContext('2d')
    const half = texSize / 2

    const gradient = ctx.createRadialGradient(half, half, 0, half, half, half)
    gradient.addColorStop(0, 'rgba(255,255,255,1)')  // bright glow center
    gradient.addColorStop(0.005, from)                 // body kicks in
    gradient.addColorStop(0.4, from)                   // body held
    gradient.addColorStop(1, to)                       // fades to edge

    ctx.fillStyle = gradient
    ctx.arc(half, half, half, 0, Math.PI * 2)
    ctx.fill()

    const texture = new THREE.Texture(canvas)
    texture.needsUpdate = true

    return new THREE.PointsMaterial({
        sizeAttenuation: false,
        size,
        transparent: true,
        blending: THREE.AdditiveBlending,
        depthTest: true,
        depthWrite: false,
        map: texture,
    })

}

export class ViewAsShots {

    cfg: iArgs
    obj: any = {}
    Maptalks

    constructor(cfg: iArgs) {
        this.cfg = cfg
        this.Maptalks = cfg.MapView
    }

    remove = (key: string) => {

        if (this.obj.hasOwnProperty(key)) {

            const group = this.obj[key]
            this.Maptalks?.threeLayer.removeMesh(group)
            const nodes: any = []
            group.traverse((child: any) => { nodes.push(child) })
            nodes.forEach((node: any) => { node.removeFromParent() })
            group.clear()
            delete this.obj[key]

        }

    }

    remove_all = () => {
        for (const x in this.obj) this.remove(x)
    }

    render_all = async (key = '', { plan, actual }: any) => {

        if (this.obj.hasOwnProperty(key)) this.remove(key)

        const status: any = {
            key,
            name: key,
            total: 0,
            drilled: 0,
            drilled_shots: [],
        }

        const group = new THREE.Group()

        let is_3D_enabled = KeyValue('3D') === 'yes'
        let is_3D_diff = Number(KeyValue('Elevation'))
        this.obj[key] = group

        const altitude = (el: number) => {
            const elevation = el
            const vector3 = this.Maptalks?.threeLayer.distanceToVector3(elevation, elevation)
            const zPos = vector3.x
            return zPos
        }

        const add_cylinder = (name: string, layer: string, { x = 0, y = 0, z = 0, h = 0, s = 0 }) => {

            const height = altitude(h)
            const geometry = new THREE.CylinderGeometry(s, s, height, 8, 1)
            let material = null

            if (layer === 'target') material = new THREE.MeshStandardMaterial({
                color: 'grey',
                wireframe: true,
                transparent: true,
                opacity: 0.5
            });
            else if (layer === 'actual') material = new THREE.MeshPhongMaterial({
                color: 'green',
                wireframe: true,
                transparent: true,
                opacity: 0.5
            });
            else material = new THREE.MeshBasicMaterial({ color: LAYER_PRESETS[layer] ?? '#d48806' })

            const cylinder = new THREE.Mesh(geometry, material)
            cylinder.name = name
            cylinder.rotateX(Math.PI / 2)
            cylinder.position.set(x, y, z - (height / 2))
            group.add(cylinder)

        }

        const add_text = (text: string, { x = 0, y = 0, z = 0, c = '#444', s = 1 }) => {

            const label = createTextLabel(text, c, s)
            label.position.set(x, y, z)
            group.add(label)

        }

        const [nm, lt] = KeyValue('Zone').split(',')

        plan.forEach(([n, x, y, z, h]: csvItems) => {

            const { lat, lng } = UTM.convertUtmToLatLng(x, y, `${nm}`, `${lt}`)
            const elev = z - (h / 2)
            const aU = [x, y, elev]
            const oL = { x: lng, y: lat, z: elev }

            const alt = is_3D_enabled ? altitude(z - is_3D_diff) : 0
            const ralt = is_3D_enabled ? altitude(z - is_3D_diff + 1) : 0
            const cfg: any = { x: oL.x, y: oL.y, z: 0 }
            const f = this.Maptalks?.threeLayer.coordinateToVector3(cfg, 0)

            status.total++

            if (actual[n]) {

                const data = actual[n]
                const { netDrillMs, totalPausedMs, finalDepth, entries } = data
                const sorted = entries.sort((a: any, b: any) => b.depth - a.depth)
                const filtered = sorted.filter(({ type }: any) => type === 'layer')

                data.target = h
                status.drilled_shots.push(data)

                // console.log(n, data, sorted)

                filtered.forEach(({ depth, layer }: any, i: number) => {
                    add_cylinder(n, layer, { x: f.x, y: f.y, z: alt, h: depth, s: 0.01 + (0.00025 * (++i)) })
                })

                add_cylinder(n, 'target', { x: f.x, y: f.y, z: alt, h: h, s: 0.005 })
                add_cylinder(n, 'actual', { x: f.x, y: f.y, z: alt, h: finalDepth, s: 0.0075 })
                add_text(`${n}`, { x: f.x, y: f.y, z: ralt, c: 'green', s: 1 })

                status.drilled++

            } else {
                add_cylinder(n, 'target', { x: f.x, y: f.y, z: alt, h: h, s: 0.005 })
                add_text(`${n}`, { x: f.x, y: f.y, z: ralt, s: 1 })
            }

        })

        this.obj[`report-${key}`] = { name: key, data: { status, shots: plan } }
        this.Maptalks?.threeLayer.addMesh(group)

    }

    report = (key: string) => {

        this.cfg.event.emit('report', this.obj[`report-${key}`])

    }


    addWithFilter = (key: string, ref = 'UTM', res: any[]) => {

        const obj: any = {}

        for (const x of res) {

            const { qr, rows } = x

            if (qr.path === 'get-chunks-merged') {

                obj['plan'] = CSV_GeoJson_Parser(rows)

            }

            if (qr.path === 'get-events-shot-actual') {

                const acts: any = {}
                for (const x of rows) {
                    const shot = JSON.parse(x.data)
                    acts[shot.holeId] = shot
                }

                obj['actual'] = acts

            }

        }

        this.render_all(key, obj)

    }

    removeStartsWith = (key: string) => {

        if (this.obj) for (const x in this.obj) if (x.indexOf(key) === 0) {

            this.cfg.MapView?.threeLayer.removeMesh(this.obj[x])
            delete this.obj[x]

        }

    }

}